// client/src/coms/components/sidebar_parts/serverNavModel.js
// Shared server-navigation state and actions used by both server navigation layouts:
// the default dropdown (SidebarTopDrawer) and the optional vertical rail (ServerRail).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { comsApiDelete, comsApiGet, comsApiPatch, comsApiPut } from '../../comsApi';
import { clampMenuPos, getServerLogoUrl, makeAcronym, safeArray } from './sidebarUtils';
import { useMyPermissions, ADMIN_PERMISSION_KEYS } from '../../usePermissions';

export function toInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : fallback;
}

export function getUnreadCountFromServerShape(s) {
  if (!s || typeof s !== 'object') return 0;
  const v =
    s.unread_count != null
      ? s.unread_count
      : s.unreadCount != null
        ? s.unreadCount
        : s.unread != null
          ? s.unread
          : 0;
  const n = toInt(v, 0);
  return n > 0 ? n : 0;
}

export function getMentionCountFromServerShape(s) {
  if (!s || typeof s !== 'object') return 0;
  const v =
    s.mention_count != null
      ? s.mention_count
      : s.mentionCount != null
        ? s.mentionCount
        : s.mentions != null
          ? s.mentions
          : 0;
  const n = toInt(v, 0);
  return n > 0 ? n : 0;
}

export function formatMentionBadge(count) {
  const n = Math.max(0, toInt(count, 0));
  if (n <= 0) return '';
  if (n > 99) return '99+';
  return String(n);
}

function isOwnerServerShapeLegacy(s) {
  if (!s || typeof s !== 'object') return false;

  const v = (k) => s[k];

  const truthyOwnerFlags = [v('is_owner'), v('isOwner'), v('owner'), v('am_owner'), v('amOwner')].some(
    (x) => x === true || x === 1
  );
  if (truthyOwnerFlags) return true;

  const role = String(v('my_role') || v('role') || v('membership_role') || '').toLowerCase();
  if (role === 'owner') return true;

  const perm = String(v('my_permission') || v('permission') || '').toLowerCase();
  if (perm === 'owner') return true;

  const ownerId =
    v('owner_user_id') != null
      ? String(v('owner_user_id'))
      : v('owner_id') != null
        ? String(v('owner_id'))
        : '';
  const myId =
    v('my_user_id') != null ? String(v('my_user_id')) : v('user_id') != null ? String(v('user_id')) : '';
  if (ownerId && myId && ownerId === myId) return true;

  return false;
}

function getOwnerUserIdFromServerShape(s) {
  if (!s || typeof s !== 'object') return 0;
  const v =
    s.owner_user_id != null
      ? s.owner_user_id
      : s.ownerId != null
        ? s.ownerId
        : s.owner_userId != null
          ? s.owner_userId
          : s.ownerUserId != null
            ? s.ownerUserId
            : s.owner_id != null
              ? s.owner_id
              : 0;
  return toInt(v, 0);
}

function getMyUserIdFromServerShape(s) {
  if (!s || typeof s !== 'object') return 0;
  const v =
    s.my_user_id != null
      ? s.my_user_id
      : s.myUserId != null
        ? s.myUserId
        : s.user_id != null
          ? s.user_id
          : s.userId != null
            ? s.userId
            : 0;
  return toInt(v, 0);
}

export function normalizeNavServer(s) {
  const o = s && typeof s === 'object' ? s : {};
  const id =
    o.id != null
      ? String(o.id)
      : o.server_id != null
        ? String(o.server_id)
        : o.public_id != null
          ? String(o.public_id)
          : '';
  const name = String(o.name || '').trim();
  const logo = getServerLogoUrl(o);
  const unreadCount = getUnreadCountFromServerShape(o);
  const ownerId = getOwnerUserIdFromServerShape(o);
  const myId = getMyUserIdFromServerShape(o);
  const myDisplayName =
    o.my_display_name != null
      ? String(o.my_display_name)
      : o.myDisplayName != null
        ? String(o.myDisplayName)
        : '';

  return {
    ...o,
    id,
    name,
    logo,
    unreadCount,
    ownerId,
    myId,
    myDisplayName,
    isOwnerLegacy: isOwnerServerShapeLegacy(o),
    acronym: String(o.acronym || '').trim() || makeAcronym(name),
  };
}

export function navServersShallowEqual(a, b) {
  const aa = safeArray(a).map(normalizeNavServer);
  const bb = safeArray(b).map(normalizeNavServer);
  if (aa.length !== bb.length) return false;

  for (let i = 0; i < aa.length; i += 1) {
    if (aa[i].id !== bb[i].id) return false;
    if (aa[i].name !== bb[i].name) return false;
    if (aa[i].logo !== bb[i].logo) return false;
    if (aa[i].acronym !== bb[i].acronym) return false;
    if (aa[i].unreadCount !== bb[i].unreadCount) return false;
    if (aa[i].ownerId !== bb[i].ownerId) return false;
    if (aa[i].myId !== bb[i].myId) return false;
    if (aa[i].myDisplayName !== bb[i].myDisplayName) return false;
    if (aa[i].isOwnerLegacy !== bb[i].isOwnerLegacy) return false;
  }

  return true;
}

// Returns a new list with the server `serverId` moved to `toIndex` (clamped). Returns the
// original list unchanged when the id is unknown or the position would not change.
export function moveServerInList(list, serverId, toIndex) {
  const arr = safeArray(list);
  const sid = String(serverId || '').trim();
  const from = arr.findIndex((s) => String(s && s.id != null ? s.id : '').trim() === sid);
  if (from < 0) return arr;

  const target = Math.max(0, Math.min(arr.length - 1, toInt(toIndex, from)));
  if (target === from) return arr;

  const next = arr.slice();
  const [item] = next.splice(from, 1);
  next.splice(target, 0, item);
  return next;
}

async function fetchMeUser() {
  try {
    const resA = await fetch('/api/user/me', { credentials: 'include', cache: 'no-store' });
    if (resA.ok) {
      const a = await resA.json().catch(() => null);
      if (a && (a.id != null || a.user_id != null)) return a;
    }
  } catch {}

  try {
    const resB = await fetch('/api/me', { credentials: 'include', cache: 'no-store' });
    if (resB.ok) {
      const b = await resB.json().catch(() => null);
      if (b && (b.id != null || b.user_id != null)) return b;
    }
  } catch {}

  return null;
}

export function LogoFill({ url, alt, size = '100%', onBroken }) {
  const [ok, setOk] = useState(false);

  useEffect(() => {
    let alive = true;

    if (!url) {
      setOk(false);
      return () => {
        alive = false;
      };
    }

    const img = new Image();

    img.onload = () => {
      if (!alive) return;
      setOk(true);
    };

    img.onerror = () => {
      if (!alive) return;
      setOk(false);
      if (typeof onBroken === 'function') onBroken();
    };

    img.src = url;

    return () => {
      alive = false;
    };
  }, [url, onBroken]);

  if (!url || !ok) return null;

  return (
    <span
      aria-label={alt || ''}
      role={alt ? 'img' : undefined}
      style={{
        position: 'absolute',
        inset: 0,
        display: 'block',
        backgroundImage: `url("${url}")`,
        backgroundSize: typeof size === 'number' ? `${size}px ${size}px` : size === '100%' ? 'cover' : 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        pointerEvents: 'none',
      }}
    />
  );
}

export function useServerNavModel({
  servers,
  activeServerId,
  onSelectServer,
  onServersChanged,
  apiAvailable,
  user,
  serverUnreadById,
  serverMentionCountById,
  rootRef,
}) {
  const [navServers, setNavServers] = useState(() => safeArray(servers).map(normalizeNavServer));
  const [remoteServersLoaded, setRemoteServersLoaded] = useState(false);
  const [fetchedLogoByServerId, setFetchedLogoByServerId] = useState({});
  const [brokenLogoKeys, setBrokenLogoKeys] = useState({});
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuX, setMenuX] = useState(0);
  const [menuY, setMenuY] = useState(0);
  const [menuServer, setMenuServer] = useState(null);
  const [menuBusy, setMenuBusy] = useState(false);
  const [menuErr, setMenuErr] = useState('');
  const [orderErr, setOrderErr] = useState('');
  const [me, setMe] = useState(null);

  const menuRef = useRef(null);
  const refreshBusyRef = useRef(false);
  const orderSaveSeqRef = useRef(0);
  const orderSavingRef = useRef(false);

  const effectiveServers = useMemo(() => safeArray(navServers).map(normalizeNavServer), [navServers]);

  useEffect(() => {
    if (!remoteServersLoaded) {
      setNavServers(safeArray(servers).map(normalizeNavServer));
    }
  }, [servers, remoteServersLoaded]);

  const refreshServersNow = useCallback(async () => {
    if (refreshBusyRef.current) return;
    // Do not let a background refresh overwrite an optimistic reorder that is still saving.
    if (orderSavingRef.current) return;
    refreshBusyRef.current = true;
    try {
      const all = await comsApiGet('/servers').catch(() => null);
      if (orderSavingRef.current) return;
      const next = all && all.ok && Array.isArray(all.servers) ? all.servers.map(normalizeNavServer) : [];
      setNavServers((prev) => (navServersShallowEqual(prev, next) ? prev : next));
      setRemoteServersLoaded(true);
    } catch {
      setRemoteServersLoaded(true);
    } finally {
      refreshBusyRef.current = false;
    }
  }, []);

  useEffect(() => {
    let alive = true;
    let t = null;

    async function tick() {
      if (!alive) return;
      await refreshServersNow();
    }

    tick();
    t = setInterval(tick, 30000);

    function onFocus() {
      tick();
    }

    function onVisibility() {
      try {
        if (document.visibilityState === 'visible') tick();
      } catch {
        tick();
      }
    }

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      alive = false;
      if (t) clearInterval(t);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshServersNow]);

  useEffect(() => {
    let alive = true;

    (async () => {
      const u = await fetchMeUser();
      if (!alive) return;
      setMe(u);
    })().catch(() => {});

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    async function loadMissingLogos() {
      const list = safeArray(navServers).map(normalizeNavServer);
      if (!list.length) return;

      const idsToFetch = list
        .map((s) => String(s.id || '').trim())
        .filter(Boolean)
        .filter((sid) => {
          const found = list.find((x) => String(x.id || '') === sid);
          const direct = found && found.logo ? String(found.logo).trim() : '';
          if (direct) return false;
          if (Object.prototype.hasOwnProperty.call(fetchedLogoByServerId, sid)) return false;
          return true;
        });

      if (!idsToFetch.length) return;

      const results = await Promise.all(
        idsToFetch.map(async (sid) => {
          try {
            const r = await comsApiGet(`/server-logo/${encodeURIComponent(sid)}`);
            const logo = r && r.logo_url ? String(r.logo_url).trim() : '';
            return [sid, logo];
          } catch {
            return [sid, ''];
          }
        })
      );

      if (!alive) return;

      setFetchedLogoByServerId((prev) => {
        const next = { ...(prev || {}) };
        for (const [sid, logo] of results) {
          if (!sid) continue;
          next[String(sid)] = String(logo || '');
        }
        return next;
      });
    }

    loadMissingLogos().catch(() => {});

    return () => {
      alive = false;
    };
  }, [navServers, fetchedLogoByServerId]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setMenuServer(null);
    setMenuBusy(false);
    setMenuErr('');
  }, []);

  useEffect(() => {
    if (!menuOpen) return;

    function isInsideMenu(target) {
      const el = menuRef.current;
      if (!el) return false;
      try {
        return !!(target && el.contains(target));
      } catch {
        return false;
      }
    }

    function isInsideRoot(target) {
      try {
        const rootEl = rootRef && rootRef.current;
        return !!(rootEl && target && rootEl.contains(target));
      } catch {
        return false;
      }
    }

    function onDown(e) {
      if (isInsideMenu(e?.target)) return;
      if (isInsideRoot(e?.target)) return;
      closeMenu();
    }

    function onContext(e) {
      if (isInsideMenu(e?.target)) return;
      if (isInsideRoot(e?.target)) return;
      closeMenu();
    }

    function onKey(e) {
      if (e && e.key === 'Escape') closeMenu();
    }

    function onScroll(e) {
      if (isInsideMenu(e?.target)) return;
      closeMenu();
    }

    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('contextmenu', onContext, true);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);

    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('contextmenu', onContext, true);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [menuOpen, closeMenu, rootRef]);

  const myUserId = useMemo(() => {
    const u = user && typeof user === 'object' ? user : null;
    const idFromProp = u && (u.id != null ? u.id : u.user_id != null ? u.user_id : null);
    const idFromMe =
      me && typeof me === 'object' ? (me.id != null ? me.id : me.user_id != null ? me.user_id : null) : null;
    return toInt(idFromProp != null ? idFromProp : idFromMe, 0);
  }, [user, me]);

  const isOwnerOfServer = useCallback(
    (s) => {
      if (!s || typeof s !== 'object') return false;

      const ownerUserId = getOwnerUserIdFromServerShape(s);
      if (myUserId > 0 && ownerUserId > 0) return myUserId === ownerUserId;

      const myFromServer = getMyUserIdFromServerShape(s);
      if (myFromServer > 0 && ownerUserId > 0) return myFromServer === ownerUserId;

      return isOwnerServerShapeLegacy(s);
    },
    [myUserId]
  );

  const canLeaveServer = useCallback(
    (s) => {
      if (!s || typeof s !== 'object') return false;

      const ownerUserId = getOwnerUserIdFromServerShape(s);

      if (myUserId > 0 && ownerUserId > 0) return myUserId !== ownerUserId;

      const myFromServer = getMyUserIdFromServerShape(s);
      if (myFromServer > 0 && ownerUserId > 0) return myFromServer !== ownerUserId;

      return false;
    },
    [myUserId]
  );

  const reloadServers = useCallback(
    async (payload) => {
      try {
        const data = await comsApiGet('/servers');
        const next = Array.isArray(data && data.servers) ? data.servers.map(normalizeNavServer) : [];
        setNavServers(next);
        setRemoteServersLoaded(true);
        setFetchedLogoByServerId({});
        setBrokenLogoKeys({});
        if (typeof onServersChanged === 'function') {
          await onServersChanged({ ...(payload || {}), servers: next });
        }
      } catch (e) {
        if (typeof onServersChanged === 'function') {
          await onServersChanged({ ...(payload || {}), reloadError: e?.message || String(e) });
        }
      }
    },
    [onServersChanged]
  );

  const unreadMap = useMemo(() => {
    const input = serverUnreadById && typeof serverUnreadById === 'object' ? serverUnreadById : {};
    const next = {};
    for (const key of Object.keys(input)) {
      const sid = String(key || '').trim();
      if (!sid) continue;
      next[sid] = toInt(input[key], 0) > 0 ? 1 : 0;
    }
    return next;
  }, [serverUnreadById]);

  const mentionCountMap = useMemo(() => {
    const input = serverMentionCountById && typeof serverMentionCountById === 'object' ? serverMentionCountById : {};
    const next = {};
    for (const key of Object.keys(input)) {
      const sid = String(key || '').trim();
      if (!sid) continue;
      next[sid] = Math.max(0, toInt(input[key], 0));
    }
    return next;
  }, [serverMentionCountById]);

  const anyUnread = useMemo(() => {
    const ids = Object.keys(unreadMap);
    if (ids.length) return ids.some((sid) => toInt(unreadMap[sid], 0) > 0);
    return safeArray(effectiveServers).some((srv) => getUnreadCountFromServerShape(srv) > 0);
  }, [effectiveServers, unreadMap]);

  const totalMentionCount = useMemo(() => {
    const ids = Object.keys(mentionCountMap);
    if (ids.length) {
      return ids.reduce((sum, sid) => sum + Math.max(0, toInt(mentionCountMap[sid], 0)), 0);
    }
    return safeArray(effectiveServers).reduce((sum, srv) => sum + getMentionCountFromServerShape(srv), 0);
  }, [effectiveServers, mentionCountMap]);

  const getServerBadge = useCallback(
    (srv) => {
      const sid = String(srv && srv.id != null ? srv.id : '').trim();
      const showUnread = Object.prototype.hasOwnProperty.call(unreadMap, sid)
        ? toInt(unreadMap[sid], 0) > 0
        : getUnreadCountFromServerShape(srv) > 0;
      const mentionCount = Object.prototype.hasOwnProperty.call(mentionCountMap, sid)
        ? Math.max(0, toInt(mentionCountMap[sid], 0))
        : getMentionCountFromServerShape(srv);
      return {
        showUnread,
        mentionCount,
        showMention: mentionCount > 0,
        mentionText: formatMentionBadge(mentionCount),
      };
    },
    [unreadMap, mentionCountMap]
  );

  const getServerLogo = useCallback(
    (srv) => {
      const sid = String(srv && srv.id != null ? srv.id : '').trim();
      const raw = String(
        (srv && srv.logo ? srv.logo : '') || (fetchedLogoByServerId[sid] ? fetchedLogoByServerId[sid] : '') || ''
      ).trim();
      return raw && !brokenLogoKeys[sid] ? raw : '';
    },
    [fetchedLogoByServerId, brokenLogoKeys]
  );

  const markLogoBroken = useCallback((sid) => {
    const key = String(sid || '').trim();
    if (!key) return;
    setBrokenLogoKeys((prev) => (prev && prev[key] ? prev : { ...(prev || {}), [key]: true }));
  }, []);

  const selectServer = useCallback(
    (sid) => {
      const nextSid = String(sid || '').trim();
      if (!nextSid) return;

      setNavServers((prev) =>
        safeArray(prev).map((srv) =>
          String(srv && srv.id != null ? srv.id : '').trim() === nextSid
            ? { ...srv, unread_count: 0, unreadCount: 0, unread: 0, mention_count: 0, mentionCount: 0, mentions: 0 }
            : srv
        )
      );

      closeMenu();

      if (typeof onSelectServer === 'function') {
        onSelectServer(nextSid);
      }
    },
    [closeMenu, onSelectServer]
  );

  const openMenuAt = useCallback((x, y, s) => {
    setMenuErr('');
    setMenuBusy(false);
    setMenuServer(s);
    setMenuOpen(true);
    setMenuX(Number.isFinite(x) ? x : 0);
    setMenuY(Number.isFinite(y) ? y : 0);
  }, []);

  const openServerContextMenu = useCallback(
    (e, s) => {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();

      let x = e && Number.isFinite(e.clientX) ? e.clientX : 0;
      let y = e && Number.isFinite(e.clientY) ? e.clientY : 0;

      // Keyboard-invoked context menus report 0,0; anchor to the element instead.
      if (!x && !y && e && e.currentTarget && typeof e.currentTarget.getBoundingClientRect === 'function') {
        const r = e.currentTarget.getBoundingClientRect();
        x = Math.round(r.right + 6);
        y = Math.round(r.top);
      }

      openMenuAt(x, y, s);
    },
    [openMenuAt]
  );

  const moveServer = useCallback(
    async (sid, toIndex) => {
      const prevList = safeArray(navServers);
      const nextList = moveServerInList(prevList, sid, toIndex);
      if (nextList === prevList) return;

      setOrderErr('');
      setNavServers(nextList);

      const seq = orderSaveSeqRef.current + 1;
      orderSaveSeqRef.current = seq;
      orderSavingRef.current = true;

      try {
        await comsApiPut('/servers/order', {
          server_ids: nextList.map((s) => String(s && s.id != null ? s.id : '').trim()).filter(Boolean),
        });
        if (orderSaveSeqRef.current !== seq) return;
        orderSavingRef.current = false;
        if (typeof onServersChanged === 'function') {
          await onServersChanged({ reorderedServerId: String(sid || ''), servers: nextList });
        }
      } catch (e) {
        if (orderSaveSeqRef.current !== seq) return;
        orderSavingRef.current = false;
        setNavServers(prevList);
        setOrderErr(e && e.status === 404 ? 'Server ordering is not available yet' : 'Could not save server order');
      }
    },
    [navServers, onServersChanged]
  );

  const clearOrderErr = useCallback(() => setOrderErr(''), []);

  const busyMessage = apiAvailable === false ? 'API not available (trying anyway)' : '';

  const doChangeMyName = useCallback(async () => {
    if (!menuServer) return;

    if (!myUserId) {
      setMenuErr('You must be logged in');
      return;
    }

    const current =
      (menuServer && menuServer.my_display_name != null ? String(menuServer.my_display_name) : '') ||
      (menuServer && menuServer.myDisplayName != null ? String(menuServer.myDisplayName) : '') ||
      '';

    const next = window.prompt('Set your name for this server (leave blank to reset)', current);
    if (next == null) return;

    const name = String(next || '').trim();

    setMenuBusy(true);
    setMenuErr(busyMessage);

    try {
      const sid = String(menuServer.id);
      await comsApiPatch(`/servers/${encodeURIComponent(sid)}/me`, { display_name: name });
      closeMenu();
      await reloadServers({ changedMyNameServerId: sid, forceReload: true });
    } catch (e) {
      setMenuErr(e?.message || 'Failed to update your name');
      setMenuBusy(false);
    }
  }, [menuServer, myUserId, busyMessage, closeMenu, reloadServers]);

  const doRename = useCallback(async () => {
    if (!menuServer) return;

    if (!isOwnerOfServer(menuServer)) {
      setMenuErr('Only the owner can rename this server');
      return;
    }

    const next = window.prompt('Rename server', String(menuServer.name || ''));
    if (next == null) return;

    const name = String(next || '').trim();
    if (!name) {
      setMenuErr('Name cannot be empty');
      return;
    }
    if (name.length > 128) {
      setMenuErr('Name too long');
      return;
    }

    setMenuBusy(true);
    setMenuErr(busyMessage);

    try {
      const sid = String(menuServer.id);

      await comsApiPatch(`/servers/${encodeURIComponent(sid)}`, { name });

      setNavServers((prev) =>
        safeArray(prev).map((s) => {
          if (String(s.id) !== sid) return s;
          const nextS = { ...s, name };
          if (s.acronym != null) nextS.acronym = makeAcronym(name);
          return nextS;
        })
      );

      closeMenu();
      await reloadServers({ renamedServerId: sid, forceReload: true });
    } catch (e) {
      setMenuErr(e?.message || 'Rename failed');
      setMenuBusy(false);
    }
  }, [menuServer, isOwnerOfServer, busyMessage, closeMenu, reloadServers]);

  const removeServerLocally = useCallback((sid) => {
    setFetchedLogoByServerId((prev) => {
      const next = { ...(prev || {}) };
      delete next[sid];
      return next;
    });
    setBrokenLogoKeys((prev) => {
      const next = { ...(prev || {}) };
      delete next[sid];
      return next;
    });
  }, []);

  const pickFallbackSelection = useCallback(
    (removedId, remaining) => {
      if (String(activeServerId) === removedId) {
        const fallback = remaining[0] || null;
        return fallback ? String(fallback.id) : null;
      }
      return String(activeServerId || '') || null;
    },
    [activeServerId]
  );

  const doDelete = useCallback(async () => {
    if (!menuServer) return;

    if (!isOwnerOfServer(menuServer)) {
      setMenuErr('Only the owner can delete this server');
      return;
    }

    const ok = window.confirm(`Delete server "${menuServer.name}"? This cannot be undone.`);
    if (!ok) return;

    const deletedId = String(menuServer.id);
    const remainingNow = safeArray(navServers).filter((x) => String(x.id) !== deletedId);
    const selectServerId = pickFallbackSelection(deletedId, remainingNow);

    setMenuBusy(true);
    setMenuErr(busyMessage);

    try {
      await comsApiDelete(`/servers/${encodeURIComponent(deletedId)}`);

      setNavServers(remainingNow);
      removeServerLocally(deletedId);

      closeMenu();
      await reloadServers({ deletedServerId: deletedId, selectServerId, forceReload: true });

      if (selectServerId && typeof onSelectServer === 'function') {
        onSelectServer(selectServerId);
      }
    } catch (e) {
      setMenuErr(e?.message || 'Delete failed');
      setMenuBusy(false);
    }
  }, [
    menuServer,
    isOwnerOfServer,
    navServers,
    pickFallbackSelection,
    busyMessage,
    removeServerLocally,
    closeMenu,
    reloadServers,
    onSelectServer,
  ]);

  const doLeave = useCallback(async () => {
    if (!menuServer) return;

    if (isOwnerOfServer(menuServer)) {
      setMenuErr('Owners must delete the server instead');
      return;
    }

    if (!canLeaveServer(menuServer)) {
      setMenuErr('You cannot leave this server');
      return;
    }

    const ok = window.confirm(`Leave server "${menuServer.name}"?`);
    if (!ok) return;

    const sid = String(menuServer.id);
    const remainingNow = safeArray(navServers).filter((x) => String(x.id) !== sid);
    const selectServerId = pickFallbackSelection(sid, remainingNow);

    setMenuBusy(true);
    setMenuErr(busyMessage);

    try {
      await comsApiDelete(`/servers/${encodeURIComponent(sid)}/me`);

      setNavServers(remainingNow);
      removeServerLocally(sid);

      closeMenu();
      await reloadServers({ leftServerId: sid, selectServerId, forceReload: true });

      if (selectServerId && typeof onSelectServer === 'function') {
        onSelectServer(selectServerId);
      }
    } catch (e) {
      setMenuErr(e?.message || 'Leave failed');
      setMenuBusy(false);
    }
  }, [
    menuServer,
    isOwnerOfServer,
    canLeaveServer,
    navServers,
    pickFallbackSelection,
    busyMessage,
    removeServerLocally,
    closeMenu,
    reloadServers,
    onSelectServer,
  ]);

  return {
    servers: effectiveServers,
    myUserId,
    anyUnread,
    totalMentionCount,
    getServerBadge,
    getServerLogo,
    markLogoBroken,
    selectServer,
    moveServer,
    orderErr,
    clearOrderErr,
    isOwnerOfServer,
    canLeaveServer,
    menu: {
      ref: menuRef,
      open: menuOpen,
      x: menuX,
      y: menuY,
      server: menuServer,
      busy: menuBusy,
      err: menuErr,
    },
    openServerContextMenu,
    openMenuAt,
    closeMenu,
    doChangeMyName,
    doRename,
    doDelete,
    doLeave,
  };
}

const MENU_ITEM_STYLE = {
  width: '100%',
  height: 36,
  border: 0,
  background: 'transparent',
  color: 'inherit',
  textAlign: 'left',
  padding: '0 10px',
  borderRadius: 8,
  cursor: 'pointer',
};

// Context menu shared by both navigation layouts. Only actions the caller is permitted to
// perform are listed; the server enforces the same rules independently.
export function ServerContextMenu({ nav, onOpenServerSettings, onMoveServer, activeServerId }) {
  const { menu } = nav;
  const srv = menu.server;
  const firstItemRef = useRef(null);
  // Managers, not only the owner, can open Server Settings.
  const menuPerms = useMyPermissions(menu.open && srv ? String(srv.id || '') : '');

  useEffect(() => {
    if (!menu.open) return;
    try {
      if (firstItemRef.current) firstItemRef.current.focus({ preventScroll: true });
    } catch {}
  }, [menu.open, srv]);

  if (!menu.open || !srv) return null;

  const sid = String(srv.id || '');
  const isOwner = nav.isOwnerOfServer(srv);
  const canLeave = nav.canLeaveServer(srv);
  const canChangeName = nav.myUserId > 0;
  const idx = nav.servers.findIndex((s) => String(s.id) === sid);
  const canMove = typeof onMoveServer === 'function' && idx >= 0;

  const items = [];
  const canManage = isOwner || (menuPerms.loaded && ADMIN_PERMISSION_KEYS.some((k) => menuPerms.has(k)));
  if (canManage && typeof onOpenServerSettings === 'function') {
    items.push({ key: 'settings', label: 'Server settings', onClick: () => onOpenServerSettings(sid) });
  }
  items.push({
    key: 'events',
    label: 'Events',
    onClick: () => {
      window.dispatchEvent(new CustomEvent('vox-open-target', { detail: { kind: 'server', server_key: sid } }));
      setTimeout(() => window.dispatchEvent(new CustomEvent('vox-open-server-events')), 50);
    },
  });
  if (canChangeName) items.push({ key: 'nick', label: 'Change my server name', onClick: nav.doChangeMyName });
  if (canMove && idx > 0) {
    items.push({ key: 'up', label: 'Move up', onClick: () => onMoveServer(sid, idx - 1) });
  }
  if (canMove && idx < nav.servers.length - 1) {
    items.push({ key: 'down', label: 'Move down', onClick: () => onMoveServer(sid, idx + 1) });
  }
  // Per-server push notification level (server-enforced; see /servers/:key/notifications).
  const setNotify = (level) => comsApiPut(`/servers/${encodeURIComponent(sid)}/notifications`, { level }).catch(() => {});
  items.push({ key: 'notify-all', label: 'Notify: all messages', onClick: () => setNotify('all') });
  items.push({ key: 'notify-mentions', label: 'Notify: only @mentions', onClick: () => setNotify('mentions') });
  items.push({ key: 'notify-none', label: 'Mute server notifications', onClick: () => setNotify('none') });
  if (isOwner) items.push({ key: 'rename', label: 'Rename server', onClick: nav.doRename });
  if (!isOwner) items.push({ key: 'report', label: 'Report server content', onClick: () => window.dispatchEvent(new CustomEvent('vox-report', { detail: { type: 'server', serverKey: sid, ref: 'server', label: srv.name || '' } })) });
  if (canLeave) items.push({ key: 'leave', label: 'Leave server', onClick: nav.doLeave });
  if (isOwner) items.push({ key: 'delete', label: 'Delete server', onClick: nav.doDelete, danger: true });

  const w = 220;
  const h = 48 + items.length * 36 + (menu.err ? 40 : 0);
  const pos = clampMenuPos(menu.x, menu.y, w, h);

  function onMenuKeyDown(e) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const buttons = Array.from(menu.ref.current ? menu.ref.current.querySelectorAll('button') : []);
    if (!buttons.length) return;
    e.preventDefault();
    const at = buttons.indexOf(document.activeElement);
    const nextIdx = e.key === 'ArrowDown' ? (at + 1) % buttons.length : (at - 1 + buttons.length) % buttons.length;
    buttons[nextIdx].focus();
  }

  return (
    <div
      ref={menu.ref}
      role="menu"
      aria-label={`${srv.name || 'Server'} options`}
      onKeyDown={onMenuKeyDown}
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: w,
        background: 'var(--vxs-1, #0a0f16)',
        border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
        borderRadius: 12,
        boxShadow: '0 18px 40px rgba(0,0,0,0.45)',
        padding: 8,
        zIndex: 2000,
        color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.96 * var(--vx-ov, 1)))',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          padding: '8px 10px 10px',
          fontSize: 13,
          fontWeight: 800,
          opacity: 0.9,
          borderBottom: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
          marginBottom: 6,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {String(srv.name || 'Server')}
        {String(activeServerId || '') === sid ? <span style={{ opacity: 0.55, fontWeight: 600 }}> · current</span> : null}
      </div>

      {items.map((item, i) => (
        <button
          key={item.key}
          ref={i === 0 ? firstItemRef : undefined}
          type="button"
          role="menuitem"
          className="coms-server-menu-item"
          onClick={item.onClick}
          disabled={menu.busy}
          style={{ ...MENU_ITEM_STYLE, color: item.danger ? '#ff7875' : 'inherit' }}
        >
          {item.label}
        </button>
      ))}

      {menu.err ? (
        <div
          role="alert"
          style={{
            marginTop: 8,
            padding: '0 10px 6px',
            fontSize: 12,
            color: '#ff7875',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {menu.err}
        </div>
      ) : null}
    </div>
  );
}
